import React, { useContext, useState, useEffect } from "react";
import { auth } from "../../firebase/firebase";
import { GoogleAuthProvider, User } from "firebase/auth";
import { AuthProvider, onAuthStateChanged } from "firebase/auth";
import { AccountView } from "../../firestore/Types/AccountSchema";
import { getAccount } from "../../firestore/Functions/accountFunctions";
import { PermissionSchema } from "../../firestore/Types/PermissionSchema";
import { getPermission } from "../../firestore/Functions/permissionFunctions";
import { LoggedInProvider } from "./LoggedInContext";

const authContext = React.createContext(
    {
        userLoggedIn: false,
        isEmailUser: false,
        isGoogleUser: false,
        currentUser: {} as User,
        account: {} as AccountView,
        permission: {} as PermissionSchema,
        refreshAccount: async () => {} 
    }
);

export function useAuth() {
    return useContext(authContext);
  }

export function AppAuthProvider({ children }: { children: React.ReactNode }) {
    const [currentUser, setCurrentUser] = useState({} as User);
    const [account, setAccount] = useState<AccountView>({} as AccountView);
    const [permission, setPermission] = useState({} as PermissionSchema);
    const [userLoggedIn, setUserLoggedIn] = useState(false);
    const [isEmailUser, setIsEmailUser] = useState(false);
    const [isGoogleUser, setIsGoogleUser] = useState(false);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, initializeUser);
        return unsubscribe;
      }, []);
      
    async function initializeUser(user: User | null) {
        if (user) {
            setCurrentUser(user);

            // check if provider is email and password login
            const isEmail = user.providerData.some(
                (provider: AuthProvider) => provider.providerId === "password"
            );
            setIsEmailUser(isEmail);

            //check if the auth provider is google or not
            const isGoogle = user.providerData.some(
                (provider: AuthProvider) => provider.providerId === GoogleAuthProvider.PROVIDER_ID
            );
            setIsGoogleUser(isGoogle);

            setUserLoggedIn(true);

            //check if the user has an account
            // if they don't, create one

            const userAccount = await getAccount();

            if (userAccount) {
                setAccount(userAccount as AccountView);
            }

            //check if the user has a permission
            // if they don't, create one

            const userPermission = await getPermission();

            if (userPermission) {
                setPermission(userPermission as PermissionSchema);
            }

        } else {
            setCurrentUser({} as User);
            setAccount({} as AccountView);
            setUserLoggedIn(false);
        }

        setLoading(false);
    }

    async function refreshAccount() {
        if (!currentUser) {
            return;
        }

        const userAccount = await getAccount();
        if (userAccount) {
            setAccount(userAccount as AccountView);
        }

        const userPermission = await getPermission();
        if (userPermission) {
            setPermission(userPermission as PermissionSchema);
        }
    }

    const value = {
        userLoggedIn,
        isEmailUser,
        isGoogleUser,
        currentUser,
        account,
        permission,
        refreshAccount
    };

    if (userLoggedIn === true) {
        return (
            <authContext.Provider value={value}>
                <LoggedInProvider>
                    {!loading && children}
                </LoggedInProvider>
            </authContext.Provider>
        );
    }

    return (
    <authContext.Provider value={value}>
        {!loading && children}
    </authContext.Provider>
    );
}